import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Divider,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Email,
  Settings,
  Business,
  Description,
  Security,
  Help,
  QuestionAnswer,
  VideoLibrary,
  ContactSupport,
  GetApp,
  Code,
  Link,
  Dashboard,
} from '@mui/icons-material';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  tags: string[];
}

const HelpPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  const faqData: FAQItem[] = [
    {
      id: 'email-config',
      category: 'Email Configuration',
      question: 'How do I configure email integration?',
      answer: `To configure email integration:

1. **Go to Settings** → **Email Sync**
2. **Choose your email provider** (Gmail, Outlook, Exchange)
3. **Enter your credentials**:
   - Email: your-email@company.com
   - Password: Your email password or app password
   - SMTP Server: (auto-detected for common providers)
   - Port: (auto-detected)
4. **Test the connection** using the "Test Connection" button
5. **Save settings** once the test is successful

**Gmail Configuration**:
- SMTP Server: smtp.gmail.com
- Port: 587 (TLS) or 465 (SSL)
- Enable "Less secure app access" OR use App Password
- For 2FA: Generate App Password in Google Account settings

**Outlook/Office 365**:
- SMTP Server: smtp-mail.outlook.com
- Port: 587
- Use your regular Microsoft account credentials
- Modern authentication supported

**Exchange Server**:
- SMTP Server: Your company's Exchange server
- Port: Usually 587 or 25
- Contact IT department for exact settings
- May require domain\\username format

**Common Issues & Solutions**:
- **"Authentication failed"**: Check username/password, try app password
- **"Connection timeout"**: Check firewall, try different port
- **"SSL/TLS errors"**: Verify encryption settings match provider
- **Corporate networks**: May block SMTP, contact IT department
- **Two-factor authentication**: Use app-specific passwords

**Testing Your Configuration**:
1. Click "Test Connection" button
2. Check for success message
3. Send a test email to verify
4. Monitor logs for any errors`,
      tags: ['email', 'configuration', 'setup', 'integration']
    },
    {
      id: 'business-plan-save',
      category: 'Business Plans',
      question: 'How do I save and access my business plans?',
      answer: `Business plans are automatically saved as you work:

**Saving**:
- Plans auto-save when you complete each step
- Click "Save Progress" button on any step for manual save
- Use "Finish" button when all sections are complete

**Accessing Saved Plans**:
1. **In Business Planning App**: Click "My Plans" in the navigation
2. **In CRM**: Go to Documents section and filter by "Business Plans"
3. **Direct Link**: http://localhost:5174/my-plans

**Plan Status**:
- **Draft**: Incomplete plan, still being worked on
- **Completed**: All sections filled out (100% completion)
- **Submitted**: Plan has been submitted for review

**Syncing with CRM**:
- Plans automatically sync with CRM when authentication is configured
- View linked documents in CRM Documents section
- Search across all business plans from CRM`,
      tags: ['business-plan', 'save', 'access', 'sync']
    },
    {
      id: 'document-management',
      category: 'Document Management',
      question: 'How do I upload and organize documents?',
      answer: `Document management allows you to store and organize files:

**Uploading Documents**:
1. Go to **Documents** section in CRM
2. Click **"Upload Document"** button
3. Select file and fill in metadata:
   - Name and description
   - Document type (contract, proposal, etc.)
   - Tags for easy searching
4. **Link to entities** (optional):
   - Account ID: Link to specific customer
   - Business Plan ID: Attach to business plan
   - Contact ID: Associate with contact

**Organizing Documents**:
- Use **tags** for categorization
- **Filter by type**: Business Plans, Contracts, Proposals
- **Search** by name, description, or content
- **Sort by date** or relevance

**Document Categories**:
- **All**: View all documents
- **Business Plans**: Plans and related documents
- **Accounts**: Customer-specific documents
- **Contacts**: Personal documents
- **Unlinked**: Documents not associated with any entity`,
      tags: ['documents', 'upload', 'organize', 'files']
    },
    {
      id: 'authentication',
      category: 'Authentication',
      question: 'How do I set up authentication between apps?',
      answer: `To enable cross-app functionality, set up authentication:

**Step 1: Get Authentication Token**
1. Open browser Developer Tools (F12)
2. Go to Console tab
3. Paste this code:

\`\`\`javascript
// Get fresh token
fetch('http://localhost:8002/auth/token', {
  method: 'POST',
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  body: 'username=admin@example.com&password=admin123'
})
.then(r => r.json())
.then(data => {
  const token = data.access_token;
  localStorage.setItem('authToken', token);
  localStorage.setItem('crmAuthToken', token);
  localStorage.setItem('auth_token', token);
  localStorage.setItem('access_token', token);
  localStorage.setItem('user', JSON.stringify({
    id: 1, email: 'admin@example.com', full_name: 'Admin User', role: 'admin', is_admin: true
  }));
  localStorage.setItem('isAuthenticated', 'true');
  console.log('✅ Authentication configured!');
  location.reload();
});
\`\`\`

**Step 2: Apply to Both Apps**
- Run the code in **CRM** (localhost:5173)
- Run the code in **Business Planning** (localhost:5174)
- Refresh both pages

**Verification**:
- Check that Documents section loads without errors
- Verify business plan sync works
- Test cross-app navigation`,
      tags: ['authentication', 'setup', 'token', 'login']
    },
    {
      id: 'tasks-management',
      category: 'Task Management',
      question: 'How do I create and manage tasks?',
      answer: `Task management helps you track work and deadlines:

**Creating Tasks**:
1. Go to **Tasks** section
2. Click **"Create Task"** button
3. Fill in details:
   - Title and description
   - Priority (High, Medium, Low)
   - Due date
   - Assign to team member
   - Link to account or deal

**Managing Tasks**:
- **My Tasks**: View tasks assigned to you
- **Overdue**: See tasks past their due date
- **Filter**: By status, priority, assignee
- **Update**: Change status, priority, or details
- **Complete**: Mark tasks as done

**Task Status**:
- **Pending**: Not started
- **In Progress**: Currently being worked on
- **Completed**: Finished
- **Cancelled**: No longer needed

**Best Practices**:
- Set realistic due dates
- Use clear, actionable titles
- Add detailed descriptions
- Link tasks to relevant accounts/deals
- Regular review of overdue tasks`,
      tags: ['tasks', 'management', 'productivity', 'deadlines']
    },
    {
      id: 'crm-navigation',
      category: 'Navigation',
      question: 'How do I navigate between different sections?',
      answer: `The CRM has several main sections accessible from the top navigation:

**Main Sections**:
- **Dashboard**: Overview and key metrics
- **Accounts**: Customer and company management
- **Contacts**: Individual contact management
- **Deals**: Sales pipeline and opportunities
- **Tasks**: Task and project management
- **Documents**: File storage and organization

**Quick Navigation Tips**:
- Use the **search bar** to find specific records
- **Breadcrumbs** show your current location
- **Back button** returns to previous page
- **Keyboard shortcuts**: Ctrl+K for quick search

**Cross-App Navigation**:
- **Business Planning**: Create and manage business plans
- **CRM Documents**: Access from business plan wizard
- **Shared Authentication**: Single login for both apps

**Mobile Navigation**:
- Responsive design works on tablets and phones
- Collapsible menu on smaller screens
- Touch-friendly interface`,
      tags: ['navigation', 'interface', 'sections', 'menu']
    },
    {
      id: 'global-search',
      category: 'Navigation',
      question: 'How do I use the global search feature?',
      answer: `The global search bar in the top navigation allows you to quickly find any record:

**Using Global Search**:
1. **Click the search bar** in the top navigation
2. **Type at least 2 characters** to start searching
3. **Select from results** to navigate directly to the record
4. **Use keyboard shortcut**: Press Ctrl+K (or Cmd+K on Mac) to focus search

**What You Can Search**:
- **Accounts**: Company names, domains, industries
- **Contacts**: Names, email addresses, job titles
- **Deals**: Deal names, descriptions, amounts
- **Tasks**: Task titles, descriptions, priorities

**Search Tips**:
- **Partial matches**: Search works with partial text
- **Case insensitive**: Capitalization doesn't matter
- **Multiple words**: Search across all fields
- **Quick navigation**: Click any result to go directly to that record

**Keyboard Shortcuts**:
- **Ctrl+K** (Windows/Linux) or **Cmd+K** (Mac): Focus search bar
- **Arrow keys**: Navigate through search results
- **Enter**: Select highlighted result
- **Escape**: Close search dropdown

**Search Results Show**:
- **Record name** and **type** (Account, Contact, Deal, Task)
- **Description** with relevant details
- **Metadata** like amounts, priorities, or domains

**Advanced Search**:
- Use the search within specific sections for more filters
- Each section (Accounts, Contacts, etc.) has its own advanced search
- Global search is for quick navigation across all data`,
      tags: ['search', 'navigation', 'keyboard', 'shortcuts', 'global']
    },
    {
      id: 'knowledge-base-text',
      category: 'Knowledge Base',
      question: 'How do I add client notes, call transcripts, and unstructured data to the CRM?',
      answer: `NEW FEATURE: You can now paste text directly into the CRM's AI knowledge base without uploading files!

**What You Can Add**:
• Client phone call transcripts
• Meeting notes and action items
• Email thread summaries
• Sales call notes and follow-ups
• Product requirements from clients
• Client feedback and testimonials
• Competitive intelligence from conversations
• Deal notes and context

**How to Add Text (3 Easy Steps)**:

**Step 1**: Navigate to the Knowledge Base section
- Look for the "Knowledge Base" or "Add Information" option in your CRM menu

**Step 2**: Click "Add Text" button
- Opens a dialog where you can paste your text directly

**Step 3**: Fill in the details:
- **Text**: Paste your content (min 10 chars, max 50,000 chars)
- **Title**: Give it a descriptive name (e.g., "TechCorp Discovery Call - Jan 2024")
- **Category**: Choose from:
  • client_info - General client information
  • sales_call - Sales call notes and outcomes
  • meeting_notes - Meeting minutes and discussions
  • email - Email thread summaries
  • product_specs - Product requirements and specifications
  • requirements - Client requirements and needs
  • general - Other business information
- **Tags**: Add searchable tags (e.g., "techcorp", "enterprise", "Q1-2024")
- **Description** (optional): Add context

**Example - Client Call Notes**:

Title: TechCorp Discovery Call - Sarah Johnson
Category: sales_call
Tags: techcorp, discovery, enterprise, crm-pain-points

Text:
"Discovery call with Sarah Johnson, VP Operations at TechCorp.
Pain points discussed:
- Manual data entry taking 5 hours/week per rep
- No ERP integration (using SAP)
- Limited reporting capabilities
- Current solution: Salesforce (too expensive) and HubSpot (missing features)

Budget: $50K annually
Decision timeline: End of Q1 2024
Competition: Evaluating 3 vendors including us
Next steps: Send proposal by Friday, schedule technical demo next week"

**Why This Matters**:
✅ AI agents can reference this information in future conversations
✅ No need to re-explain client context every time
✅ Searchable and always accessible
✅ Builds institutional memory of client relationships
✅ Better insights from AI based on complete client history

**Pro Tips**:
• Use categories to route information correctly (sales_call info goes to sales knowledge)
• Add tags for easy searching later ("enterprise", "Q1-2024", client name)
• Paste immediately after client calls while details are fresh
• Include action items and next steps
• Reference budget, timeline, and decision-makers

**Backend API** (for developers):
POST http://localhost:8002/api/v1/knowledge-base/add-text
Body: { "text": "...", "title": "...", "category": "sales_call", "tags": [...] }`,
      tags: ['knowledge-base', 'client-notes', 'unstructured-data', 'ai', 'new-feature', 'sales-calls', 'meeting-notes']
    }
  ];

  const categories = [
    'All',
    'Email Configuration',
    'Business Plans',
    'Document Management',
    'Authentication',
    'Task Management',
    'Navigation',
    'Knowledge Base'
  ];

  const quickStartGuides = [
    {
      title: 'Getting Started with CRM',
      description: 'Complete setup guide for new users',
      steps: [
        'Set up authentication between apps',
        'Configure email integration',
        'Create your first account',
        'Add contacts and deals',
        'Upload important documents'
      ]
    },
    {
      title: 'Business Plan Creation',
      description: 'Step-by-step business plan guide',
      steps: [
        'Access Business Planning app',
        'Start with Executive Summary',
        'Complete all 9 sections',
        'Upload supporting documents',
        'Save and sync with CRM'
      ]
    },
    {
      title: 'Document Organization',
      description: 'Best practices for file management',
      steps: [
        'Upload documents with clear names',
        'Use tags for categorization',
        'Link documents to relevant entities',
        'Set up folder structure',
        'Regular cleanup and archiving'
      ]
    }
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = activeTab === 0 || faq.category === categories[activeTab];

    return matchesSearch && matchesCategory;
  });

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Help & FAQ
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Find answers to common questions and get help with using the CRM system
      </Typography>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <VideoLibrary sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Video Tutorials</Typography>
            <Typography variant="body2" color="text.secondary">
              Watch step-by-step guides
            </Typography>
            <Button size="small" sx={{ mt: 1 }}>Coming Soon</Button>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <ContactSupport sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Contact Support</Typography>
            <Typography variant="body2" color="text.secondary">
              Get help from our team
            </Typography>
            <Button size="small" sx={{ mt: 1 }}>support@crm.com</Button>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <GetApp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">User Manual</Typography>
            <Typography variant="body2" color="text.secondary">
              Download complete guide
            </Typography>
            <Button size="small" sx={{ mt: 1 }}>Download PDF</Button>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Code sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">API Documentation</Typography>
            <Typography variant="body2" color="text.secondary">
              Developer resources
            </Typography>
            <Button size="small" sx={{ mt: 1 }} href="http://localhost:8002/docs" target="_blank">
              View API Docs
            </Button>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Start Guides */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Quick Start Guides
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStartGuides.map((guide, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {guide.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {guide.description}
                </Typography>
                <List dense>
                  {guide.steps.map((step, stepIndex) => (
                    <ListItem key={stepIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {stepIndex + 1}.
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={step}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* FAQ Section */}
      <Typography variant="h5" gutterBottom>
        Frequently Asked Questions
      </Typography>

      {/* Search and Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search FAQ..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {categories.map((category, index) => (
            <Tab key={index} label={category} />
          ))}
        </Tabs>
      </Box>

      {/* FAQ Accordions */}
      {filteredFAQs.length === 0 ? (
        <Alert severity="info">
          No FAQ items found matching your search criteria.
        </Alert>
      ) : (
        <Box>
          {filteredFAQs.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expandedAccordion === faq.id}
              onChange={handleAccordionChange(faq.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {faq.question}
                  </Typography>
                  <Chip
                    label={faq.category}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 2 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                    {faq.answer}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {faq.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                        onClick={() => setSearchQuery(tag)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Contact Support */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" gutterBottom>
          Still Need Help?
        </Typography>
        <Typography variant="body1" paragraph>
          Can't find what you're looking for? Our support team is here to help you get the most out of your CRM system.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<Email />}
            onClick={() => window.location.href = 'mailto:info@executiveforceai.com?subject=CRM Support Request&body=Please describe your issue or question:'}
          >
            Email Support
          </Button>
          <Button 
            variant="outlined" 
            sx={{ color: 'white', borderColor: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'white' } }}
            startIcon={<ContactSupport />}
            onClick={() => window.location.href = 'mailto:info@executiveforceai.com?subject=Feature Request - Force CRM&body=Please describe the feature you would like to see:'}
          >
            Feature Request
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default HelpPage;
